'use client';

import { useTranslations } from 'next-intl';
import { BuildingOffice2Icon, MapPinIcon } from '@heroicons/react/24/outline';
import { cardVariants } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import type { EventHostKind } from '@/app/[locale]/event/[eventId]/libs/eventHost';
import type { EventVenue } from '@/app/[locale]/event/[eventId]/libs/venues';
import { directionsHref, telHref } from '@/app/[locale]/event/[eventId]/libs/venuePage';

interface EventVenuesCardProps {
  /** The venues listed in the Venues tab only (`venueCardOf`). */
  venues: EventVenue[];
  /** The event's owner — who runs it, a club or a federation (`venueCardOf`). */
  hostName?: string | null;
  /** Whether the owner is a club or a governing body; drives the caption under the name. */
  hostKind?: EventHostKind | null;
  /** The event location's address and contacts, only while no venue is listed (`venueCardOf`). */
  hostPlace?: EventVenue | null;
  /** The owner club is the place: one "Hosted at" block instead of two (`venueCardOf`). */
  hostIsPlace?: boolean;
  /** Public page card treatment; shared admin previews stay plain by default. */
  presentation?: 'plain' | 'card';
  className?: string;
}

/** Address plus the Directions / website / phone links of a venue or the event location. */
function PlaceDetails({ place: v }: { place: EventVenue }) {
  const t = useTranslations('Event');
  return (
    <>
      {v.address && <p className="text-muted text-xs">{v.address}</p>}
      <p className="flex flex-wrap gap-x-3 text-xs">
        <a
          className="text-accent underline"
          href={directionsHref(v)}
          target="_blank"
          rel="noopener noreferrer"
        >
          {t('venues.directions')}
        </a>
        {v.location?.website && (
          <a
            className="text-accent underline"
            href={v.location.website}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('venues.website')}
          </a>
        )}
        {v.location?.phone && (
          <a
            className="text-accent underline"
            href={telHref(v.location.phone)}
            target="_blank"
            rel="noopener noreferrer"
          >
            {v.location.phone}
          </a>
        )}
      </p>
    </>
  );
}

/**
 * Event page "Hosted by" + "Venue" summary card: separately labelled blocks so who runs the event is
 * never mistaken for the place it is played. Hosted by = the event's owner (a club or a governing
 * body, captioned as such); Venue = the venues listed in the Venues tab, or, while none is listed,
 * the event location with its address and links (`hostPlace`). When the owner is that same club
 * (`hostIsPlace`) the two collapse into one "Hosted at" block. Nothing when there is neither. The
 * venue block lists every venue by name and address; the full venue pages (photo, courts, note,
 * directions) live on the Venues tab, which the tab bar already links.
 */
export function EventVenuesCard({
  venues,
  hostName,
  hostKind,
  hostPlace,
  hostIsPlace = false,
  presentation = 'plain',
  className,
}: EventVenuesCardProps) {
  const t = useTranslations('Event');
  if (venues.length === 0 && !hostName) return null;
  const label = 'text-muted flex items-center gap-1.5 text-xs font-medium tracking-wide uppercase';
  const oneCard = hostIsPlace && !!hostName && !!hostPlace && venues.length === 0;
  const separate = 'border-border border-t pt-3';

  return (
    <section
      className={cn(
        'space-y-3',
        presentation === 'card'
          ? cn(cardVariants.base, 'p-4 md:p-6')
          : 'border-border border-t py-4',
        className,
      )}
    >
      {oneCard ? (
        <div className="space-y-1">
          <p className={label}>
            <BuildingOffice2Icon className="h-4 w-4" />
            {t('venues.hostedAt')}
          </p>
          <p className="text-foreground text-sm font-semibold">{hostName}</p>
          <PlaceDetails place={hostPlace} />
        </div>
      ) : (
        <>
          {hostName && (
            <div className="space-y-1">
              <p className={label}>
                <BuildingOffice2Icon className="h-4 w-4" />
                {t('venues.hostedBy')}
              </p>
              <p className="text-foreground text-sm font-semibold">{hostName}</p>
              {hostKind && (
                <p className="text-muted text-xs">
                  {t(hostKind === 'club' ? 'venues.hostKindClub' : 'venues.hostKindBody')}
                </p>
              )}
            </div>
          )}
          {venues.length === 0 && hostPlace && (
            <div className={cn('space-y-1', hostName && separate)}>
              <p className={label}>
                <MapPinIcon className="h-4 w-4" />
                {t('venues.title')}
              </p>
              <p className="text-foreground text-sm font-semibold break-words">{hostPlace.name}</p>
              <PlaceDetails place={hostPlace} />
            </div>
          )}
          {venues.length > 0 && (
            <p className={cn(label, hostName && separate)}>
              <MapPinIcon className="h-4 w-4" />
              {venues.length > 1
                ? t('venues.titleMany', { count: venues.length })
                : t('venues.title')}
            </p>
          )}
          {venues.length > 0 && (
            <div className="space-y-0.5">
              <ul data-venue-list="all" className="space-y-2">
                {venues.map((v) => (
                  <li key={v.id} className="space-y-0.5">
                    <p className="text-foreground text-sm font-semibold break-words">{v.name}</p>
                    {v.address && <p className="text-muted text-xs break-words">{v.address}</p>}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </section>
  );
}
